import { useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { CONTRACT_TYPE } from '../../constants';
import { useTokenStore, useUserInfoStore, useWorkInfoStore } from '../../store';
import { initialWorkInfoState } from '~/store/workInfo/useWorkInfoStore';
import { defaultErrorHandler } from '~/utils';

import { useGetCommuteInfo, useGetUserLatestContract, useUserInfo } from '../api';

/**
 * @description - 앱 최초 실행 시, 분기 조건을 판단하여 이동되어야 할 페이지의 경로를 반환하는 커스텀훅
 * @remarks 토큰 존재 여부, 계약 구분, 로그인 여부 등을 판단하여 페이지 이동 경로를 반환합니다.
 * - 토큰이 존재하지 않을 때 : 로그인 페이지로 이동
 * - 계약 구분이 아르바이트일 때 : 현재 계약 상태이면 출근 페이지로 이동, 아니면 내부망 연결 페이지로 이동
 * - 계약 구분이 상용직일 때 : 현재 출근 상태이면 출근 페이지로 이동, 아니면 내부망 연결 페이지로 이동
 */
export const useGetEntryRedirectPath = () => {
  const [redirectPath, setRedirectPath] = useState<null | string>(null);
  const [isSuccess, setIsSuccess] = useState<null | boolean>(null);

  const accessToken = useTokenStore((state) => state.accessToken);
  const { contractType, loginId } = useUserInfoStore(
    useShallow((state) => ({
      contractType: state.contractType,
      loginId: state.loginId,
    }))
  );

  const { setUserInfo } = useUserInfoStore((state) => state.actions);
  const { setWorkInfo } = useWorkInfoStore((state) => state.actions);

  const requestGetUserInfo = useUserInfo();
  const requestGetCommuteInfo = useGetCommuteInfo();
  const requestGetUserLatestContract = useGetUserLatestContract();

  const getEntryRedirectPath = async () => {
    if (!accessToken) {
      setRedirectPath('/login');
      setIsSuccess(true);
      return;
    }

    if (contractType === CONTRACT_TYPE.SHORT) {
      const isCurrentContract = await getIsCurrentContract();

      if (isCurrentContract === null || typeof isCurrentContract === 'undefined') {
        setRedirectPath('/login');
        setIsSuccess(false);
        return;
      }

      if (isCurrentContract) {
        await requestGetUserLatestContract({
          showLoading: false,
          onSuccess: (response) => {
            if (response) {
              setUserInfo({ latestContract: response.data });
            }
          },
          onError: (error) => {
            console.error('>> 최신 계약 정보 조회 실패', error);
          },
        });
      }
      setRedirectPath(isCurrentContract ? '/start-work' : '/check-network');
      setIsSuccess(true);
      return;
    }

    if (contractType === CONTRACT_TYPE.CONTRACT && loginId !== '') {
      const isCurrentCommute = await getIsCurrentCommute();

      if (isCurrentCommute === null || typeof isCurrentCommute === 'undefined') {
        setRedirectPath('/login');
        setIsSuccess(false);
        return;
      }

      setRedirectPath(isCurrentCommute ? '/start-work' : '/check-network');
      setIsSuccess(true);
      return;
    }

    setRedirectPath('/login');
    setIsSuccess(false);
  };

  const getIsCurrentContract = async () => {
    return new Promise((resolve) =>
      requestGetUserInfo({
        showLoading: false,
        onSuccess: (response) => {
          if (response) {
            setUserInfo({
              currentContractStatus: response.currentContractStatus ?? null,
              safetyEducation: response.safetyEducation,
            });
            const isCurrentContract = response.currentContractStatus?.code === 'CURRENT';
            return resolve(isCurrentContract);
          }
        },
        onError: (error) => {
          console.error('>> 현재 계약 상태 확인 실패', error);
          defaultErrorHandler(error);
          return resolve(null);
        },
      })
    );
  };

  const getIsCurrentCommute = async () => {
    return new Promise((resolve) =>
      requestGetCommuteInfo({
        showLoading: false,
        onSuccess: (response) => {
          const { status, data, recentCheckInOut, workType } = response.data;
          const isCommute = status?.code === 'START_WORK';

          setWorkInfo({
            status,
            recentCheckInOut,
            workType,
            ...(data ?? initialWorkInfoState),
          });
          return resolve(isCommute);
        },
        onError: (error) => {
          console.error('>> 출근 정보 조회 실패', error);
          defaultErrorHandler(error);

          return resolve(null);
        },
      })
    );
  };

  return { redirectPath, isSuccess, getEntryRedirectPath };
};
